import { supabase } from '../../config/supabase';

const PAGE_SIZE = 1000;

const fetchAll = async (queryFactory, pageSize = PAGE_SIZE) => {
    let from = 0;
    let rows = [];

    while (true) {
        const to = from + pageSize - 1;
        const { data, error } = await queryFactory().range(from, to);
        if (error) throw error;

        const batch = data || [];
        rows = rows.concat(batch);

        if (batch.length < pageSize) break;
        from += pageSize;
    }

    return rows;
};

export const getDateWindow = (startDate, endDate) => {
    if (!startDate && !endDate) return { fromIso: null, toIso: null };

    const start = startDate ? new Date(startDate) : new Date(endDate);
    start.setHours(0, 0, 0, 0);

    const end = endDate ? new Date(endDate) : new Date(startDate);
    end.setHours(23, 59, 59, 999);

    return { fromIso: start.toISOString(), toIso: end.toISOString() };
};

const applyDateWindow = (query, field, fromIso, toIso) => {
    let next = query;
    if (fromIso) next = next.gte(field, fromIso);
    if (toIso) next = next.lte(field, toIso);
    return next;
};

const applyUserFilter = (query, selectedUserId, columns = []) => {
    if (!selectedUserId || selectedUserId === 'all' || columns.length === 0) return query;
    return query.or(columns.map(col => `${col}.eq.${selectedUserId}`).join(','));
};

const matchesSelectedUser = (row, selectedUserId, keys = []) => {
    if (!selectedUserId || selectedUserId === 'all') return true;
    return keys.some(key => row?.[key] === selectedUserId);
};

export const getCompanyUsers = async (companyId) => {
    return fetchAll(() =>
        supabase
            .from('users')
            .select('*')
            .eq('company_id', companyId)
            .order('role', { ascending: true })
            .order('full_name', { ascending: true })
    );
};

export const getCompanyAnalytics = async (companyId, filters = {}, options = {}) => {
    const { startDate = null, endDate = null, selectedUserId = 'all' } = filters;
    const { onSectionLoaded } = options;
    const { fromIso, toIso } = getDateWindow(startDate, endDate);

    try {
        const jobs = await fetchAll(() => {
            let query = supabase
                .from('jobs')
                .select('*, job_quotes(*)')
                .eq('company_id', companyId)
                .eq('deleted', false)
                .order('created_at', { ascending: false });

            query = applyDateWindow(query, 'created_at', fromIso, toIso);
            return query;
        });
        onSectionLoaded?.('jobs', jobs);

        const users = await fetchAll(() =>
            supabase
                .from('users')
                .select('*')
                .eq('company_id', companyId)
                .order('full_name', { ascending: true })
        );
        onSectionLoaded?.('users', users);

        const actionLogs = await fetchAll(() => {
            let query = supabase
                .from('job_action_logs')
                .select('*, jobs!inner(company_id, unique_id), users!job_action_logs_user_id_fkey(id, username, full_name, role)')
                .eq('jobs.company_id', companyId)
                .order('timestamp', { ascending: false });

            query = applyDateWindow(query, 'timestamp', fromIso, toIso);
            query = applyUserFilter(query, selectedUserId, ['user_id']);
            return query;
        });
        onSectionLoaded?.('actionLogs', actionLogs);

        const deliveries = await fetchAll(() => {
            let query = supabase
                .from('job_deliveries')
                .select('*, jobs!inner(company_id)')
                .eq('jobs.company_id', companyId)
                .order('delivery_date', { ascending: false, nullsFirst: false });

            query = applyDateWindow(query, 'created_at', fromIso, toIso);
            return query;
        });

        const filteredDeliveries = deliveries.filter(row =>
            matchesSelectedUser(row, selectedUserId, [
                'assigned_driver_id',
                'scheduled_by_user_id',
                'assigned_by_user_id',
            ])
        );
        onSectionLoaded?.('deliveries', filteredDeliveries);

        const smsLogs = await fetchAll(() => {
            let query = supabase
                .from('sms_logs')
                .select('*')
                .eq('company_id', companyId)
                .order('sent_at', { ascending: false });

            query = applyDateWindow(query, 'sent_at', fromIso, toIso);
            return query;
        });
        onSectionLoaded?.('smsLogs', smsLogs);

        return {
            jobs,
            users,
            actionLogs,
            deliveries: filteredDeliveries,
            smsLogs,
        };
    } catch (error) {
        console.error('Error fetching company analytics:', error);
        throw error;
    }
};

export const calculateJobStats = (jobs = []) => {
    const total = jobs.length;

    const statusCounts = jobs.reduce((acc, job) => {
        const status = job.main_status || 'unknown';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
    }, {});

    const typeCounts = jobs.reduce((acc, job) => {
        const type = job.job_type || 'unknown';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
    }, {});

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    const urgentQuote = jobs.filter(j => j.urgent_quote).length;
    const urgentDesign = jobs.filter(j => j.urgent_design).length;

    const invoiceStatus = jobs.reduce(
        (acc, job) => {
            const requiredQuotes = job.job_quotes?.filter(q => q.is_required) || [];
            if (!requiredQuotes.length) return acc;

            const invoicedQuotes = requiredQuotes.filter(
                q => q.invoiced || q.invoice_status === 'paid' || q.invoice_status === 'invoiced'
            );

            if (invoicedQuotes.length === requiredQuotes.length) acc.invoiced += 1;
            else if (invoicedQuotes.length > 0) acc.partiallyInvoiced += 1;
            else acc.notInvoiced += 1;

            return acc;
        },
        { invoiced: 0, partiallyInvoiced: 0, notInvoiced: 0 }
    );

    return {
        total,
        statusCounts,
        typeCounts,
        timeStats: {
            today: jobs.filter(j => new Date(j.created_at) >= today).length,
            thisWeek: jobs.filter(j => new Date(j.created_at) >= weekAgo).length,
            thisMonth: jobs.filter(j => new Date(j.created_at) >= monthAgo).length,
        },
        urgentStats: {
            quote: urgentQuote,
            design: urgentDesign,
            total: urgentQuote + urgentDesign,
        },
        invoiceStats: {
            ...invoiceStatus,
            invoicedPercentage: total ? ((invoiceStatus.invoiced / total) * 100).toFixed(1) : 0,
        },
        quoteSent: jobs.filter(j => j.quote_sent).length,
        siteMeasureRequired: jobs.filter(j => j.site_measure_required).length,
    };
};

export const calculateUserStats = (users = []) => {
    const total = users.length;

    const roleCounts = users.reduce((acc, user) => {
        const role = user.role || 'unknown';
        acc[role] = (acc[role] || 0) + 1;
        return acc;
    }, {});

    const statusCounts = users.reduce((acc, user) => {
        const status = user.status || 'unknown';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
    }, {});

    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const activeUsers = users.filter(
        u => u.last_active_at && new Date(u.last_active_at) >= weekAgo
    ).length;

    return {
        total,
        roleCounts,
        statusCounts,
        activeUsers,
        activePercentage: total ? ((activeUsers / total) * 100).toFixed(1) : 0,
    };
};

export const calculateDeliveryStats = (deliveries = []) => {
    const total = deliveries.length;

    const statusCounts = deliveries.reduce((acc, delivery) => {
        const status = delivery.delivery_status || 'unknown';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
    }, {});

    const completed = deliveries.filter(
        d => d.completed || d.delivery_status === 'delivered'
    ).length;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return {
        total,
        statusCounts,
        completed,
        accepted: deliveries.filter(d => d.accepted).length,
        scheduled: deliveries.filter(d => d.delivery_date).length,
        assigned: deliveries.filter(d => d.assigned_driver_id).length,
        deliveredToday: deliveries.filter(
            d => d.delivered_at && new Date(d.delivered_at) >= today
        ).length,
        completionRate: total ? ((completed / total) * 100).toFixed(1) : 0,
    };
};

export const calculateActivityStats = (actionLogs = []) => {
    const total = actionLogs.length;

    const actionCounts = actionLogs.reduce((acc, log) => {
        const action = log.action || 'unknown';
        acc[action] = (acc[action] || 0) + 1;
        return acc;
    }, {});

    const roleCounts = actionLogs.reduce((acc, log) => {
        const role = log.user_role || log.users?.role || 'unknown';
        acc[role] = (acc[role] || 0) + 1;
        return acc;
    }, {});

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

    const userActivityCounts = actionLogs.reduce((acc, log) => {
        const userKey =
            log.user_id ||
            log.users?.id ||
            log.user_name ||
            log.users?.full_name ||
            log.users?.username ||
            'unknown';

        const userName =
            log.users?.full_name ||
            log.users?.username ||
            log.user_name ||
            'Unknown';

        const userRole =
            log.users?.role ||
            log.user_role ||
            'Unknown';

        if (!acc[userKey]) {
            acc[userKey] = {
                name: userName,
                count: 0,
                role: userRole,
            };
        }

        acc[userKey].count += 1;

        return acc;
    }, {});

    return {
        total,
        actionCounts,
        roleCounts,
        actionsToday: actionLogs.filter(a => new Date(a.timestamp) >= today).length,
        actionsThisWeek: actionLogs.filter(a => new Date(a.timestamp) >= weekAgo).length,
        topUsers: Object.values(userActivityCounts)
            .sort((a, b) => b.count - a.count)
            .slice(0, 6),
    };
};

export const calculateSMSStats = (smsLogs = []) => {
    const total = smsLogs.length;

    const statusCounts = smsLogs.reduce((acc, sms) => {
        const status = sms.status || 'unknown';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
    }, {});

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

    return {
        total,
        statusCounts,
        timeStats: {
            today: smsLogs.filter(s => s.sent_at && new Date(s.sent_at) >= today).length,
            thisWeek: smsLogs.filter(s => s.sent_at && new Date(s.sent_at) >= weekAgo).length,
            thisMonth: smsLogs.filter(s => s.sent_at && new Date(s.sent_at) >= monthAgo).length,
        },
    };
};

export const getDailyTrends = (rows = [], dateField, days = 30) => {
    const trends = [];
    const now = new Date();

    for (let i = days - 1; i >= 0; i--) {
        const date = new Date(now);
        date.setDate(date.getDate() - i);

        const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);

        const count = rows.filter(row => {
            if (!row?.[dateField]) return false;
            const rowDate = new Date(row[dateField]);
            return rowDate >= dayStart && rowDate < dayEnd;
        }).length;

        trends.push({
            date: dayStart.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' }),
            count,
        });
    }

    return trends;
};

export const getJobTrends = (jobs = []) => getDailyTrends(jobs, 'created_at');

export const getActivityTrends = (actionLogs = []) => getDailyTrends(actionLogs, 'timestamp');

const buildPerformanceStats = (rows = []) => {
    const stats = {
        total: rows.length,
        started: 0,
        completed: 0,
        byUser: {},
    };

    rows.forEach(row => {
        const userKey =
            row.user_id ||
            row.designer_id ||
            row.estimator_id ||
            row.assigned_to_user_id ||
            row.users?.id ||
            row.name ||
            'unknown';

        const userName =
            row.users?.full_name ||
            row.users?.username ||
            row.name ||
            row.user_name ||
            'Unknown';

        if (!stats.byUser[userKey]) {
            stats.byUser[userKey] = {
                id: userKey,
                name: userName,
                total: 0,
                started: 0,
                completed: 0,
            };
        }

        stats.byUser[userKey].total += 1;

        if (row.is_started) {
            stats.started += 1;
            stats.byUser[userKey].started += 1;
        }

        if (row.is_completed) {
            stats.completed += 1;
            stats.byUser[userKey].completed += 1;
        }
    });

    return stats;
};

export const getPerformanceStats = async (companyId, filters = {}) => {
    const { startDate = null, endDate = null, selectedUserId = 'all' } = filters;
    const { fromIso, toIso } = getDateWindow(startDate, endDate);

    try {
        const designers = await fetchAll(() => {
            let query = supabase
                .from('job_designers')
                .select('*, jobs!inner(company_id)')
                .eq('jobs.company_id', companyId);

            query = applyDateWindow(query, 'created_at', fromIso, toIso);
            return query;
        });

        const estimators = await fetchAll(() => {
            let query = supabase
                .from('job_estimators')
                .select('*, jobs!inner(company_id)')
                .eq('jobs.company_id', companyId);

            query = applyDateWindow(query, 'created_at', fromIso, toIso);
            return query;
        });

        const filteredDesigners = designers.filter(row =>
            matchesSelectedUser(row, selectedUserId, [
                'user_id',
                'designer_id',
                'assigned_to_user_id',
            ])
        );

        const filteredEstimators = estimators.filter(row =>
            matchesSelectedUser(row, selectedUserId, [
                'user_id',
                'estimator_id',
                'assigned_to_user_id',
            ])
        );

        return {
            designers: buildPerformanceStats(filteredDesigners),
            estimators: buildPerformanceStats(filteredEstimators),
        };
    } catch (error) {
        console.error('Error fetching performance stats:', error);
        throw error;
    }
};

export const getProductionStats = async (companyId, filters = {}) => {
    const { startDate = null, endDate = null } = filters;
    const { fromIso, toIso } = getDateWindow(startDate, endDate);

    try {
        const production = await fetchAll(() => {
            let query = supabase
                .from('job_production')
                .select('*, jobs!inner(company_id)')
                .eq('jobs.company_id', companyId);

            query = applyDateWindow(query, 'created_at', fromIso, toIso);
            return query;
        });

        return {
            total: production.length,
            phases: production.reduce((acc, p) => {
                const phase = p.phase || p.production_status || 'unknown';
                acc[phase] = (acc[phase] || 0) + 1;
                return acc;
            }, {}),
            cutting: {
                started: production.filter(p => p.is_started_cutting).length,
                completed: production.filter(p => p.end_cutting_at).length,
            },
            production: {
                started: production.filter(p => p.is_started_production).length,
                completed: production.filter(p => p.is_completed).length,
            },
        };
    } catch (error) {
        console.error('Error fetching production stats:', error);
        throw error;
    }
};